import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Upload, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";

/**
 * ImageInput — drop-in replacement for a plain image_url <Input>.
 * Lets admin either paste a URL OR upload a file (jpg/png/webp).
 * On successful upload, fills value with the returned API path (/api/files/...).
 *
 * Props: { value, onChange, testId, placeholder, compact }
 */
export default function ImageInput({ value, onChange, testId = "image-input", placeholder = "Paste image URL or upload a photo", compact = false }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Pick an image file (jpg/png/webp)"); return; }
    if (file.size > 12 * 1024 * 1024) { toast.error("Max 12 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", "deal_image");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Store the RELATIVE "/api/files/<path>" — origin-independent so it works on preview AND production.
      // (Never bake REACT_APP_BACKEND_URL into stored data; the renderer resolves it against the current host.)
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const onPickFile = (e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; };
  const onDrop = (e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) upload(f); };
  const clear = () => onChange("");

  return (
    <div className={compact ? "" : "space-y-2"}>
      <div className="flex gap-2">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-[#050507] border border-[#1A1A24] rounded-md px-3 h-9 text-white text-sm"
          data-testid={`${testId}-url`}
        />
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} data-testid={`${testId}-file`} />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="px-3 h-9 rounded-md border border-emerald-500/40 text-emerald-300 text-xs hover:bg-emerald-500/10 disabled:opacity-50 inline-flex items-center gap-1.5 whitespace-nowrap"
          data-testid={`${testId}-upload-btn`}
          title="Upload from your device"
        >
          {uploading ? <span className="animate-pulse">…</span> : <><Upload className="w-3 h-3" /> Upload</>}
        </button>
      </div>

      {value ? (
        <div className="relative inline-block">
          <img src={value} alt="" className="max-h-24 rounded-md border border-[#1A1A24]" onError={(e) => { e.target.style.display = "none"; }} data-testid={`${testId}-preview`} />
          <button type="button" onClick={clear} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500/80 hover:bg-red-500 flex items-center justify-center" data-testid={`${testId}-clear`}><X className="w-3 h-3 text-white" /></button>
        </div>
      ) : (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className="border border-dashed border-white/10 rounded-md p-3 text-center cursor-pointer hover:border-white/20 transition"
          data-testid={`${testId}-dropzone`}
        >
          <ImagePlus className="w-4 h-4 text-gray-500 mx-auto mb-1" />
          <p className="text-[10px] text-gray-500">Drop image here, or click to pick · jpg/png/webp · max 12 MB</p>
        </div>
      )}
    </div>
  );
}
